import { Snackbar, contextBgColors, contextType } from '../../snackbar';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { AnalyticsPromotionName, analyticsEvents } from '../../../utils/analytics/analytics';
import { useViewTracking } from '../../../hooks/useViewTracking';
import { usePersonalDiscounts } from '../../../state/campaign/hooks/usePersonalDiscounts';
import { useAddPromotionCode } from '../../../state/order/hooks/useOrderMutation';
import { Path } from '../../../routes/components/path';
import { useOrder } from '../../../state/order/hooks/useOrder';
import { config } from '../../../utils/config';
import { usePathname } from 'next/navigation';

const creativeName = 'Personal discount banner';

export const ActiveDiscountBanner: React.FC = () => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const { intersecting: viewed } = useViewTracking(bannerRef, true, true);
  const { personalDiscounts } = usePersonalDiscounts();
  const addCouponCode = useAddPromotionCode();
  const order = useOrder();
  const pathname = usePathname();

  const personalDiscount = useMemo(() => {
    if (!config.personalDiscountBannerEnabled) return;
    if (!personalDiscounts || personalDiscounts.length < 1) return;

    return personalDiscounts[0];
  }, [personalDiscounts]);

  const isVisible = useMemo(
    () => personalDiscount !== undefined && !order.hasPromotionCode(personalDiscount.code),
    [personalDiscount, order]
  );

  useEffect(() => {
    if (!viewed || !isVisible || !bannerRef.current) return;

    analyticsEvents.viewPromotion([
      {
        promotion_name: AnalyticsPromotionName.SitewideBanner,
        creative_name: creativeName,
        creative_slot: '',
      },
    ]);
  }, [viewed, isVisible]);

  const handleClick = useCallback(
    async (isInShoppingCart: boolean) => {
      if (!personalDiscount) return;

      if (isInShoppingCart) {
        await addCouponCode(personalDiscount.code);
      }

      analyticsEvents.selectPromotion({
        promotion_name: AnalyticsPromotionName.SitewideBanner,
        creative_name: creativeName,
        creative_slot: '',
      });
    },
    [addCouponCode, personalDiscount]
  );

  useEffect(() => {
    if (!isVisible) return;

    const bgColor = contextBgColors[contextType.SUCCESS];
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bgColor);
  }, [isVisible]);

  if (!personalDiscount || !isVisible) {
    return null;
  }

  const isInShoppingCart = pathname === Path.Shoppingcart;

  return (
    <div ref={bannerRef}>
      <Snackbar
        closable={false}
        align="center"
        context={contextType.WARNING}
        onClick={() => handleClick(isInShoppingCart)}
        message={
          isInShoppingCart
            ? `[Käytä __${personalDiscount.discountName}__ koodi!]()`
            : `[Uusi etu: __${personalDiscount.discountName}__](${Path.Benefits})!`
        }
        tight={true}
      />
    </div>
  );
};
